/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
function _createMdxContent(props) {
  const _components = Object.assign({
    p: "p",
    ul: "ul",
    li: "li",
    a: "a",
    strong: "strong",
    h2: "h2",
    svg: "svg",
    path: "path",
    pre: "pre",
    code: "code",
    span: "span",
    h3: "h3",
    h4: "h4",
    blockquote: "blockquote",
    h5: "h5",
    ol: "ol"
  }, _provideComponents(), props.components), {Newsletter} = _components;
  if (!Newsletter) _missingMdxReference("Newsletter", true);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.p, {
      children: "Maintaining large React projects can be a difficult task. Below are a few practices I’ve adapted over the years working with React projects of all scales. A low of those practices were directly taken or inspired by excellent resources found in the React/Redux community, precisely:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.a, {
          href: "https://react.dev/",
          rel: "nofollow noopener noreferrer",
          target: "_blank",
          children: "React: official documentation"
        }), ";"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.a, {
          href: "https://redux.js.org/",
          rel: "nofollow noopener noreferrer",
          target: "_blank",
          children: "Redux: official documentation"
        }), ";"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.a, {
          href: "https://github.com/erikras/ducks-modular-redux",
          rel: "nofollow noopener noreferrer",
          target: "_blank",
          children: "Ducks: Redux Reducer Bundles"
        }), ";"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.a, {
          href: "https://github.com/alexnm/re-ducks",
          rel: "nofollow noopener noreferrer",
          target: "_blank",
          children: "Re-ducks: Building on the duck legacy"
        }), ";"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.a, {
          href: "https://github.com/piotrwitek/react-redux-typescript-guide",
          rel: "nofollow noopener noreferrer",
          target: "_blank",
          children: "React & Redux TypeScript guide"
        }), ";"]
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "The proposed architecture is not meant to be enforced dogmatically and is a work in progress that might change over time."
      })
    }), "\n", _jsxs(_components.h2, {
      id: "file-structure",
      children: ["File structure", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"File structure\"",
        href: "#file-structure",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["Dan Abramov created a ", _jsx(_components.a, {
        href: "https://react-file-structure.surge.sh/",
        rel: "nofollow noopener noreferrer",
        target: "_blank",
        children: "guide"
      }), " for organizing files and he made a very good point. For months I’ve been following the “good” ways to organize React projects: starting at the separation of concerns with Presentational and Container components and finishing with adapting ", _jsx(_components.a, {
        href: "https://github.com/erikras/ducks-modular-redux",
        rel: "nofollow noopener noreferrer",
        target: "_blank",
        children: "ducks"
      }), "."]
    }), "\n", _jsx(_components.p, {
      children: "It worked well for small projects, but as they grew to be 30 different, unique screens and over 200 components, it became more difficult to maintain all of this together. At Milo, we came up with a directory structure that is inspired by Django and best practices from React, taking the separation of concerns to its extreme."
    }), "\n", _jsx(_components.pre, {
      children: _jsxs(_components.code, {
        className: "code-highlight",
        children: [_jsx(_components.span, {
          className: "code-line",
          children: "src/\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "├── App.tsx\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "├── index.ts\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "├── store.ts\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "├── types.ts\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "├── shared/\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "│    └── ComponentName.tsx\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "├── modules/\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "│    └── <moduleName>/\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "│          ├── components/\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "│          │     └── ComponentName.tsx\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "│          ├── actionCreators.ts\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "│          ├── actionTypes.ts\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "│          ├── apiCalls.ts\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "│          ├── operations.ts\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "│          ├── selectors.ts\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "│          ├── reducers.ts\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "│          ├── utils.ts\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "│          ├── types.ts\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "│          └── index.ts\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "└── screens/\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "      ├── <screenNamespace>/\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "      │     ├── SubcreenNameA.tsx\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "      │     └── SubcreenNameB.tsx\n"
        }), _jsx(_components.span, {
          className: "code-line",
          children: "      └── Navigation.ts\n"
        })]
      })
    }), "\n", _jsxs(_components.h3, {
      id: "shared",
      children: ["Shared", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Shared\"",
        href: "#shared",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsx(_components.p, {
      children: "This contains the shared code used all across your app. It can include configuration files, primary presentational components (i.e. Buttons, Inputs, Grid, …) helpers to work with the API, and pretty much everything that doesn’t fit in other parts of the proposed architecture."
    }), "\n", _jsxs(_components.h3, {
      id: "screens",
      children: ["Screens", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Screens\"",
        href: "#screens",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["Screens are components that are directly mounted on routes (", _jsx(_components.a, {
        href: "https://github.com/remix-run/react-router",
        rel: "nofollow noopener noreferrer",
        target: "_blank",
        children: _jsx(_components.code, {
          children: "react-router"
        })
      }), ", ", _jsx(_components.a, {
        href: "https://github.com/react-navigation/react-navigation",
        rel: "nofollow noopener noreferrer",
        target: "_blank",
        children: _jsx(_components.code, {
          children: "react-navigation"
        })
      }), "). They render shared and/or module components."]
    }), "\n", _jsxs(_components.h3, {
      id: "modules",
      children: ["Modules", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Modules\"",
        href: "#modules",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsx(_components.p, {
      children: "Sometimes, we need to share the logic between web (React) and mobile (React Native) apps. The proposed structure makes it very easy to reuse and maintain the code without influencing other app parts."
    }), "\n", _jsxs(_components.p, {
      children: ["The main idea of ", _jsx(_components.code, {
        children: "modules/"
      }), " is to group a strongly coupled part of the application and make it as reusable as possible. It contains all the required components (later used in screens) as well as reducers, action creators, and other state-related utilities."]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "A module must contain the entire logic for handling its concept;"
      }), "\n", _jsx(_components.li, {
        children: "A module may contain all the required components to present its concept."
      }), "\n"]
    }), "\n", _jsxs(_components.h4, {
      id: "components",
      children: ["Components", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Components\"",
        href: "#components",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsx(_components.p, {
      children: "We don’t always follow the concept of a container and presentational components – the promoted thing with this concept is the separation of concerns which can be achieved in different, more maintainable ways, for example, through the Hooks API. Do what is more suitable for your case."
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsxs(_components.p, {
        children: ["“I don’t suggest splitting your components like this anymore. If you find it natural in your codebase, this pattern can be handy. But I’ve seen it enforced without any necessity and with almost dogmatic fervor far too many times. The main reason I found it useful was because it let me separate complex stateful logic from other aspects of the component. Hooks let me do the same thing without an arbitrary division.” – ", _jsx(_components.a, {
          href: "https://medium.com/@dan_abramov/smart-and-dumb-components-7ca2f9a7c7d0",
          rel: "nofollow noopener noreferrer",
          target: "_blank",
          children: "Dan Abramov"
        })]
      }), "\n"]
    }), "\n", _jsxs(_components.h4, {
      id: "index",
      children: ["Index", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Index\"",
        href: "#index",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["The ", _jsx(_components.code, {
        children: "index.ts"
      }), " file should expose the public API of a module. Everything that is not exposed in this file should be considered private and never accessed from the outside."]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "The default export must be the reducer."
      }), "\n", _jsxs(_components.li, {
        children: ["It must export ", _jsx(_components.code, {
          children: "actions"
        }), ", ", _jsx(_components.code, {
          children: "operations"
        }), ", ", _jsx(_components.code, {
          children: "selectors"
        }), ", and ", _jsx(_components.code, {
          children: "types"
        }), "."]
      }), "\n", _jsx(_components.li, {
        children: "It must expose all the components."
      }), "\n"]
    }), "\n", _jsx(_components.pre, {
      className: "language-typescript",
      children: _jsxs(_components.code, {
        className: "language-typescript code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "as"
          }), " actions ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"./actionCreators\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "as"
          }), " operations ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"./operations\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "as"
          }), " selectors ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"./selectors\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "as"
          }), " types ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"./types\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " reducer ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"./reducers\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token comment",
            children: "// Store/state-related stuff:"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "default"
          }), " reducer", _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " actions", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " operations", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " selectors", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " types ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token comment",
            children: "// Components:"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "default"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "as"
          }), " ComponentNameA ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"./components/ComponentNameA\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "default"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "as"
          }), " ComponentNameB ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"./components/ComponentNameB\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        })]
      })
    }), "\n", _jsxs(_components.h4, {
      id: "action-types",
      children: ["Action Types", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Action Types\"",
        href: "#action-types",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsx(_components.p, {
      children: "Action types are constants used by action creators and reducers. Each action type should be unique and prefixed by the project and module name."
    }), "\n", _jsx(_components.pre, {
      className: "language-typescript",
      children: _jsxs(_components.code, {
        className: "language-typescript code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " ", _jsx(_components.span, {
            className: "token constant",
            children: "POSTS_REQUEST"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"@@<project_name>/<module_name>/POSTS_REQUEST\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " ", _jsx(_components.span, {
            className: "token constant",
            children: "POSTS_PROCESS"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"@@<project_name>/<module_name>/POSTS_PROCESS\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        })]
      })
    }), "\n", _jsx(_components.p, {
      children: "Your action types should be pure string literals. Dynamic string operations (like template strings, string concatenation, etc.) will widen literal type to its supertype string. This will break contextual typing in reducer cases when using TypeScript or Flow."
    }), "\n", _jsxs(_components.h4, {
      id: "action-creators",
      children: ["Action Creators", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Action Creators\"",
        href: "#action-creators",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["The action creators should follow the ", _jsx(_components.a, {
        href: "https://github.com/redux-utilities/flux-standard-action",
        rel: "nofollow noopener noreferrer",
        target: "_blank",
        children: "Flow Standard Action"
      }), " specification when possible. Action shape should be predictable and known by the developers. Action creators should not contain any logic, nor transform the received payload – it makes them harder to test and the code is harder to debug."]
    }), "\n", _jsx(_components.pre, {
      className: "language-typescript",
      children: _jsxs(_components.code, {
        className: "language-typescript code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " createStandardAction ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"typesafe-actions\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "as"
          }), " Types ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"./actionTypes\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " Payload ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"./types\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " requestPosts ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token function",
            children: "createStandardAction"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "Types", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token constant",
            children: "POSTS_REQUEST"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), _jsx(_components.span, {
            className: "token keyword",
            children: "void"
          }), _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " processPosts ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token function",
            children: "createStandardAction"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "Types", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token constant",
            children: "POSTS_REQUEST"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), "Payload ", _jsx(_components.span, {
            className: "token operator",
            children: "|"
          }), " Error", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        })]
      })
    }), "\n", _jsxs(_components.p, {
      children: ["You should not export any default value in ", _jsx(_components.code, {
        children: "actionCreators.ts"
      }), ". Using named exports, it is easier to map dispatch to all actions exposed by a module using ", _jsx(_components.a, {
        href: "https://redux.js.org/api/bindactioncreators",
        rel: "nofollow noopener noreferrer",
        target: "_blank",
        children: _jsx(_components.code, {
          children: "bindActionCreators"
        })
      }), ", as follows:"]
    }), "\n", _jsx(_components.pre, {
      className: "language-typescript",
      children: _jsxs(_components.code, {
        className: "language-typescript code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " bindActionCreators ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"redux\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "as"
          }), " Types ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"../../types\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " actions ", _jsx(_components.span, {
            className: "token keyword",
            children: "as"
          }), " moduleActionsA ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"../moduleA\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " actions ", _jsx(_components.span, {
            className: "token keyword",
            children: "as"
          }), " moduleActionsB ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"../moduleB\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " ", _jsx(_components.span, {
            className: "token function-variable function",
            children: "mapDispatchToProps"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "dispatch", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " Dispatch", _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), "Types", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "RootAction", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token function",
            children: "bindActionCreators"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "..."
          }), "moduleActionsA", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "..."
          }), "moduleActionsB ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " dispatch", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        })]
      })
    }), "\n", _jsx(Newsletter, {}), "\n", _jsxs(_components.h4, {
      id: "api-calls",
      children: ["API Calls", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"API Calls\"",
        href: "#api-calls",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["API endpoints should not be hand-coded – it makes the code prone to errors and harder to maintain as API evolves. I encourage you to create a small configuration file with all available endpoints in ", _jsx(_components.code, {
        children: "config.ts"
      }), " file, then reuse those endpoints in ", _jsx(_components.code, {
        children: "apiCalls.ts"
      }), "."]
    }), "\n", _jsxs(_components.h5, {
      id: "configuration",
      children: ["Configuration", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Configuration\"",
        href: "#configuration",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsx(_components.pre, {
      className: "language-typescript",
      children: _jsxs(_components.code, {
        className: "language-typescript code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " ", _jsx(_components.span, {
            className: "token constant",
            children: "URL"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " ", _jsx(_components.span, {
            className: "token constant",
            children: "API"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "default"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  v1", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    posts", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsx(_components.span, {
            className: "token function",
            children: "get"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "id", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "token builtin",
            children: "number"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " meta", _jsx(_components.span, {
            className: "token operator",
            children: "?"
          }), _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " Object", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["        ", _jsx(_components.span, {
            className: "token keyword",
            children: "return"
          }), " ", _jsxs(_components.span, {
            className: "token template-string",
            children: [_jsx(_components.span, {
              className: "token template-punctuation string",
              children: "`"
            }), _jsxs(_components.span, {
              className: "token interpolation",
              children: [_jsx(_components.span, {
                className: "token interpolation-punctuation punctuation",
                children: "${"
              }), _jsx(_components.span, {
                className: "token constant",
                children: "URL"
              }), _jsx(_components.span, {
                className: "token interpolation-punctuation punctuation",
                children: "}"
              })]
            }), _jsxs(_components.span, {
              className: "token interpolation",
              children: [_jsx(_components.span, {
                className: "token interpolation-punctuation punctuation",
                children: "${"
              }), _jsx(_components.span, {
                className: "token constant",
                children: "API"
              }), _jsx(_components.span, {
                className: "token interpolation-punctuation punctuation",
                children: "}"
              })]
            }), _jsx(_components.span, {
              className: "token string",
              children: "v1/posts/"
            }), _jsxs(_components.span, {
              className: "token interpolation",
              children: [_jsx(_components.span, {
                className: "token interpolation-punctuation punctuation",
                children: "${"
              }), "id", _jsx(_components.span, {
                className: "token interpolation-punctuation punctuation",
                children: "}"
              })]
            }), _jsxs(_components.span, {
              className: "token interpolation",
              children: [_jsx(_components.span, {
                className: "token interpolation-punctuation punctuation",
                children: "${"
              }), _jsx(_components.span, {
                className: "token function",
                children: "createQueryString"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "("
              }), "meta", _jsx(_components.span, {
                className: "token punctuation",
                children: ")"
              }), _jsx(_components.span, {
                className: "token interpolation-punctuation punctuation",
                children: "}"
              })]
            }), _jsx(_components.span, {
              className: "token template-punctuation string",
              children: "`"
            })]
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsx(_components.span, {
            className: "token function",
            children: "list"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "meta", _jsx(_components.span, {
            className: "token operator",
            children: "?"
          }), _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " Object", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["        ", _jsx(_components.span, {
            className: "token keyword",
            children: "return"
          }), " ", _jsxs(_components.span, {
            className: "token template-string",
            children: [_jsx(_components.span, {
              className: "token template-punctuation string",
              children: "`"
            }), _jsxs(_components.span, {
              className: "token interpolation",
              children: [_jsx(_components.span, {
                className: "token interpolation-punctuation punctuation",
                children: "${"
              }), _jsx(_components.span, {
                className: "token constant",
                children: "URL"
              }), _jsx(_components.span, {
                className: "token interpolation-punctuation punctuation",
                children: "}"
              })]
            }), _jsxs(_components.span, {
              className: "token interpolation",
              children: [_jsx(_components.span, {
                className: "token interpolation-punctuation punctuation",
                children: "${"
              }), _jsx(_components.span, {
                className: "token constant",
                children: "API"
              }), _jsx(_components.span, {
                className: "token interpolation-punctuation punctuation",
                children: "}"
              })]
            }), _jsx(_components.span, {
              className: "token string",
              children: "v1/posts"
            }), _jsxs(_components.span, {
              className: "token interpolation",
              children: [_jsx(_components.span, {
                className: "token interpolation-punctuation punctuation",
                children: "${"
              }), _jsx(_components.span, {
                className: "token function",
                children: "createQueryString"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "("
              }), "meta", _jsx(_components.span, {
                className: "token punctuation",
                children: ")"
              }), _jsx(_components.span, {
                className: "token interpolation-punctuation punctuation",
                children: "}"
              })]
            }), _jsx(_components.span, {
              className: "token template-punctuation string",
              children: "`"
            })]
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  v2", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "token comment",
            children: "/* ... */"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        })]
      })
    }), "\n", _jsxs(_components.h5, {
      id: "api-calls-1",
      children: ["API Calls", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"API Calls\"",
        href: "#api-calls-1",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsx(_components.pre, {
      className: "language-typescript",
      children: _jsxs(_components.code, {
        className: "language-typescript code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " ", _jsx(_components.span, {
            className: "token function-variable function",
            children: "fetchPost"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "id", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "token builtin",
            children: "number"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " meta", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " Object", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token function",
            children: "fetch"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "urls", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "v1", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "posts", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token function",
            children: "get"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "id", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " meta", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token function",
            children: "then"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "response ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), " response", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token function",
            children: "json"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " ", _jsx(_components.span, {
            className: "token function-variable function",
            children: "fetchPosts"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "meta", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " Object", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token function",
            children: "fetch"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "urls", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "v1", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "posts", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token function",
            children: "list"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "meta", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token function",
            children: "then"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "response ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), " response", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token function",
            children: "json"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        })]
      })
    }), "\n", _jsxs(_components.h4, {
      id: "operations",
      children: ["Operations", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Operations\"",
        href: "#operations",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["Operations can be ", _jsx(_components.a, {
        href: "https://github.com/reduxjs/redux-thunk",
        rel: "nofollow noopener noreferrer",
        target: "_blank",
        children: "thunks"
      }), " or ", _jsx(_components.a, {
        href: "https://redux-saga.js.org/",
        rel: "nofollow noopener noreferrer",
        target: "_blank",
        children: "sagas"
      }), " and everything else that delays the action dispatch. An operation is a function that can contain logic, dispatch multiple actions based on some predicates, and manipulate their payload."]
    }), "\n", _jsx(_components.pre, {
      className: "language-typescript",
      children: _jsxs(_components.code, {
        className: "language-typescript code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "as"
          }), " Types ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"../types\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "as"
          }), " actions ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"./actionCreators\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "as"
          }), " ", _jsx(_components.span, {
            className: "token constant",
            children: "API"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"./apiCalls\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " ", _jsx(_components.span, {
            className: "token function-variable function",
            children: "doFooStuff"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "payload", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " Object", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "dispatch", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " Dispatch", _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), "Types", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "RootAction", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token function",
            children: "dispatch"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "actions", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token function",
            children: "requestPosts"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token constant",
            children: "API"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token function",
            children: "fetchPosts"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "payload", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "meta", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token function",
            children: "then"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "data ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), " ", _jsx(_components.span, {
            className: "token function",
            children: "dispatch"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "actions", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token function",
            children: "processPosts"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token function",
            children: "normalizePosts"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "data", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token function",
            children: "catch"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "err ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), " ", _jsx(_components.span, {
            className: "token function",
            children: "dispatch"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "actions", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token function",
            children: "processPosts"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "err", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "token boolean",
            children: "true"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        })]
      })
    }), "\n", _jsxs(_components.h4, {
      id: "selectors",
      children: ["Selectors", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Selectors\"",
        href: "#selectors",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["Selectors can compute derived data, allowing Redux to store the minimal possible state. A selector is not recomputed unless one of its arguments changes. It minimized the amount of component re-renders to the minimum. Have a look at the excellent ", _jsx(_components.a, {
        href: "https://github.com/reduxjs/reselect",
        rel: "nofollow noopener noreferrer",
        target: "_blank",
        children: "reselect"
      }), " package."]
    }), "\n", _jsx(_components.p, {
      children: "Consider the following example – it renders a list of posts created by the currently logged-in user:"
    }), "\n", _jsx(_components.pre, {
      className: "language-typescript",
      children: _jsxs(_components.code, {
        className: "language-typescript code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "class"
          }), " ", _jsx(_components.span, {
            className: "token class-name",
            children: "PostsList"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "extends"
          }), " ", _jsx(_components.span, {
            className: "token class-name",
            children: "React"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "PureComponent ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token function",
            children: "render"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword",
            children: "return"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), "ul", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["        ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["          ", _jsx(_components.span, {
            className: "token keyword",
            children: "this"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "props", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "posts\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["            ", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token function",
            children: "filter"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "post ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), " post", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "author ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "this"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "props", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "userId", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["            ", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token function",
            children: "map"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "post ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["              ", _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), "div", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["                ", _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), "p", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "post", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "title", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), _jsx(_components.span, {
            className: "token operator",
            children: "/"
          }), "p", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["                ", _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), "p", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "post", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "content", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), _jsx(_components.span, {
            className: "token operator",
            children: "/"
          }), "p", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["              ", _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), _jsx(_components.span, {
            className: "token operator",
            children: "/"
          }), "div", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["            ", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["        ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), _jsx(_components.span, {
            className: "token operator",
            children: "/"
          }), "ul", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " ", _jsx(_components.span, {
            className: "token function-variable function",
            children: "mapStateToProps"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " state ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  posts", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " state", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "posts", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "data", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  userId", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " state", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "auth", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "user", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "id\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        })]
      })
    }), "\n", _jsx(_components.p, {
      children: "In the example above, a render is triggered every time the post collection changes, even if the changed post is not created by the user. Using selectors, we can avoid those unnecessary re-renders and update the component only if one of the user’s posts has been created or modified:"
    }), "\n", _jsx(_components.pre, {
      className: "language-typescript",
      children: _jsxs(_components.code, {
        className: "language-typescript code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token comment",
            children: "// selectors.ts"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " createSelector ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"reselect\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " ", _jsx(_components.span, {
            className: "token function-variable function",
            children: "postsSelector"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " state ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), " state", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "posts", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "data", _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " ", _jsx(_components.span, {
            className: "token function-variable function",
            children: "userSelector"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " state ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), " state", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "auth", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "user", _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " userPostsSelector ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token function",
            children: "createSelector"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  postsSelector", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  userSelector", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "posts", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " user", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), " posts", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token function",
            children: "filter"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "post ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), " post", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "author ", _jsx(_components.span, {
            className: "token operator",
            children: "==="
          }), " user", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "id", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        })]
      })
    }), "\n", _jsx(_components.pre, {
      className: "language-typescript",
      children: _jsxs(_components.code, {
        className: "language-typescript code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token comment",
            children: "// PostsList.tsx"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "class"
          }), " ", _jsx(_components.span, {
            className: "token class-name",
            children: "PostsList"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "extends"
          }), " ", _jsx(_components.span, {
            className: "token class-name",
            children: "React"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "PureComponent ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token function",
            children: "render"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword",
            children: "return"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), "ul", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["        ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), _jsx(_components.span, {
            className: "token keyword",
            children: "this"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "props", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "userPosts", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token function",
            children: "map"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "post ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["          ", _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), "div", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["            ", _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), "p", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "post", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "title", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), _jsx(_components.span, {
            className: "token operator",
            children: "/"
          }), "p", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["            ", _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), "p", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "post", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "content", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), _jsx(_components.span, {
            className: "token operator",
            children: "/"
          }), "p", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["          ", _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), _jsx(_components.span, {
            className: "token operator",
            children: "/"
          }), "div", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["        ", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), _jsx(_components.span, {
            className: "token operator",
            children: "/"
          }), "ul", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " ", _jsx(_components.span, {
            className: "token function-variable function",
            children: "mapStateToProps"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " state ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  userPosts", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "token function",
            children: "userPostsSelector"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "state", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        })]
      })
    }), "\n", _jsx(_components.p, {
      children: "The other thing about selectors is that they facilitate the work with a part of the application that was developed by somebody else – you don’t need to know the state’s shape to work with it if the exposed selectors are enough and well-documented."
    }), "\n", _jsxs(_components.h4, {
      id: "reducers",
      children: ["Reducers", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Reducers\"",
        href: "#reducers",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["You should export one reducer per module, but a module can be composed of multiple reducers. Don’t be afraid to break your reducer into multiple chunks to reduce complexity and make it easier to test. You can always combine them using ", _jsx(_components.a, {
        href: "https://redux.js.org/api/combinereducers",
        rel: "nofollow noopener noreferrer",
        target: "_blank",
        children: _jsx(_components.code, {
          children: "combineReducers"
        })
      }), "."]
    }), "\n", _jsx(_components.pre, {
      className: "language-typescript",
      children: _jsxs(_components.code, {
        className: "language-typescript code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " combineReducers ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"redux\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " Action", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " PostsState", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " ErrorsState", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " LoadingState ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"./types\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "as"
          }), " Types ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"./actionTypes\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " ", _jsx(_components.span, {
            className: "token function-variable function",
            children: "postsReducer"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "state", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " PostsState ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " action", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " Action", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token keyword",
            children: "switch"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "action", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "type", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword",
            children: "case"
          }), " Types", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token constant",
            children: "POSTS_PROCESS"
          }), _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsx(_components.span, {
            className: "token keyword",
            children: "if"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token operator",
            children: "!"
          }), "action", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "error", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "return"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), _jsx(_components.span, {
            className: "token operator",
            children: "..."
          }), "state", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " action", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "payload", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword",
            children: "default"
          }), _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsx(_components.span, {
            className: "token keyword",
            children: "return"
          }), " state", _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " ", _jsx(_components.span, {
            className: "token function-variable function",
            children: "errorsReducer"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "state", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " ErrorsState ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "null"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " action", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " Action", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token keyword",
            children: "switch"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "action", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "type", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword",
            children: "case"
          }), " Types", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token constant",
            children: "POSTS_PROCESS"
          }), _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsx(_components.span, {
            className: "token keyword",
            children: "if"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "action", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "error", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "return"
          }), " action", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "payload", _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword",
            children: "default"
          }), _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsx(_components.span, {
            className: "token keyword",
            children: "return"
          }), " state", _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " ", _jsx(_components.span, {
            className: "token function-variable function",
            children: "loadingReducer"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "state", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " LoadingState ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token boolean",
            children: "false"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " action", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " Action", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "=>"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token keyword",
            children: "switch"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "action", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "type", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword",
            children: "case"
          }), " Types", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token constant",
            children: "POSTS_REQUEST"
          }), _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsx(_components.span, {
            className: "token keyword",
            children: "return"
          }), " ", _jsx(_components.span, {
            className: "token boolean",
            children: "true"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword",
            children: "case"
          }), " Types", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token constant",
            children: "POSTS_PROCESS"
          }), _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsx(_components.span, {
            className: "token keyword",
            children: "return"
          }), " ", _jsx(_components.span, {
            className: "token boolean",
            children: "false"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword",
            children: "default"
          }), _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsx(_components.span, {
            className: "token keyword",
            children: "return"
          }), " state", _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "default"
          }), " ", _jsx(_components.span, {
            className: "token function",
            children: "combineReducers"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  data", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " postsReducer", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  errors", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " errorsReducer", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  loading", _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " loadingReducer", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        })]
      })
    }), "\n", _jsxs(_components.h4, {
      id: "types",
      children: ["Types", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Types\"",
        href: "#types",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["If you use Flow or TypeScript, it’s a good idea to keep all the types in one place (", _jsx(_components.code, {
        children: "types.ts"
      }), "). By doing so, we can expose all of them at once to other modules of the app. This is particularly handy when we need to expose the root ", _jsx(_components.code, {
        children: "Action"
      }), " and ", _jsx(_components.code, {
        children: "State"
      }), " which is used in every selector and container. Here’s an example of ", _jsx(_components.code, {
        children: "/types.ts"
      }), ":"]
    }), "\n", _jsx(_components.pre, {
      className: "language-typescript",
      children: _jsxs(_components.code, {
        className: "language-typescript code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " AnyAction ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"redux\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " StateType ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"typesafe-actions\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " rootReducer ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"./reducers\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " types ", _jsx(_components.span, {
            className: "token keyword",
            children: "as"
          }), " FooTypes ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"../../modules/foo\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "import"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " types ", _jsx(_components.span, {
            className: "token keyword",
            children: "as"
          }), " BarTypes ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"../../modules/bar\""
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "type"
          }), " ", _jsx(_components.span, {
            className: "token class-name",
            children: "RootState"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  StateType", _jsx(_components.span, {
            className: "token operator",
            children: "<"
          }), _jsx(_components.span, {
            className: "token keyword",
            children: "typeof"
          }), " rootReducer", _jsx(_components.span, {
            className: "token operator",
            children: ">"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "type"
          }), " ", _jsx(_components.span, {
            className: "token class-name",
            children: "RootAction"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  FooTypes", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "Action ", _jsx(_components.span, {
            className: "token operator",
            children: "|"
          }), " BarTypes", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), "Action ", _jsx(_components.span, {
            className: "token operator",
            children: "|"
          }), " AnyAction", _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        })]
      })
    }), "\n", _jsxs(_components.h3, {
      id: "utilities-for-state-management",
      children: ["Utilities for state management", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Utilities for state management\"",
        href: "#utilities-for-state-management",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsx(_components.p, {
      children: "You can think of Redux as a low-level API – it doesn’t force any particular patterns and allows you pretty much to do whatever you want."
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.a, {
          href: "https://ramdajs.com/",
          rel: "nofollow noopener noreferrer",
          target: "_blank",
          children: "Ramda"
        }), ": a practical functional library for JavaScript programmers."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.a, {
          href: "https://github.com/immerjs/immer",
          rel: "nofollow noopener noreferrer",
          target: "_blank",
          children: "Immer"
        }), ": create the next immutable state by mutating the current one."]
      }), "\n"]
    }), "\n", _jsxs(_components.h3, {
      id: "utilities-for-creating-styles",
      children: ["Utilities for creating styles", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Utilities for creating styles\"",
        href: "#utilities-for-creating-styles",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["Creating styles can be a pain, especially in React Native or when you need to create custom styles based on the state. ", _jsx(_components.a, {
        href: "https://styled-components.com/ecosystem",
        rel: "nofollow noopener noreferrer",
        target: "_blank",
        children: "Styled Components"
      }), " can come in handy – they allow you to create styles directly in JavaScript using SCSS syntax."]
    }), "\n", _jsxs(_components.h3, {
      id: "tips-and-tricks",
      children: ["Tips and tricks", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Tips and tricks\"",
        href: "#tips-and-tricks",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsxs(_components.h4, {
      id: "you-can-use-reducers-on-the-inner-state",
      children: ["You can use reducers on the inner state", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"You can use reducers on the inner state\"",
        href: "#you-can-use-reducers-on-the-inner-state",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsx(_components.p, {
      children: "Creating reducers to handle the inner component state is a good practice in the case when you have complex state logic – it is easier to test and in most cases, less error-prone. Creating reducers for inner state management is even easier with the new Hooks API."
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Example:"
      }), " from official React ", _jsxs(_components.a, {
        href: "https://react.dev/reference/react/useReducer",
        rel: "nofollow noopener noreferrer",
        target: "_blank",
        children: [_jsx(_components.code, {
          children: "useReducer"
        }), " example"]
      }), ":"]
    }), "\n", _jsx(_components.pre, {
      className: "language-jsx",
      children: _jsxs(_components.code, {
        className: "language-jsx code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword module",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " initialState ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "token literal-property property",
            children: "count"
          }), _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "token number",
            children: "0"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword module",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "function"
          }), " ", _jsx(_components.span, {
            className: "token function",
            children: "reducer"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsxs(_components.span, {
            className: "token parameter",
            children: ["state", _jsx(_components.span, {
              className: "token punctuation",
              children: ","
            }), " action"]
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token keyword control-flow",
            children: "switch"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "action", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token property-access",
            children: "type"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword",
            children: "case"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"increment\""
          }), _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "token keyword control-flow",
            children: "return"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), _jsx(_components.span, {
            className: "token literal-property property",
            children: "count"
          }), _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " state", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token property-access",
            children: "count"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "+"
          }), " ", _jsx(_components.span, {
            className: "token number",
            children: "1"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword",
            children: "case"
          }), " ", _jsx(_components.span, {
            className: "token string",
            children: "\"decrement\""
          }), _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "token keyword control-flow",
            children: "return"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), _jsx(_components.span, {
            className: "token literal-property property",
            children: "count"
          }), _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " state", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token property-access",
            children: "count"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "token number",
            children: "1"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword module",
            children: "default"
          }), _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "token keyword control-flow",
            children: "throw"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "new"
          }), " ", _jsx(_components.span, {
            className: "token class-name",
            children: "Error"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword module",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "token keyword module",
            children: "default"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "function"
          }), " ", _jsx(_components.span, {
            className: "token function",
            children: _jsx(_components.span, {
              className: "token maybe-class-name",
              children: "Counter"
            })
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsxs(_components.span, {
            className: "token parameter",
            children: [_jsx(_components.span, {
              className: "token punctuation",
              children: "{"
            }), "initialState", _jsx(_components.span, {
              className: "token punctuation",
              children: "}"
            })]
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token keyword",
            children: "const"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "["
          }), "state", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " dispatch", _jsx(_components.span, {
            className: "token punctuation",
            children: "]"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token function",
            children: "useReducer"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "reducer", _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), " initialState", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token keyword control-flow",
            children: "return"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsxs(_components.span, {
            className: "token tag",
            children: [_jsx(_components.span, {
              className: "token tag",
              children: _jsx(_components.span, {
                className: "token punctuation",
                children: "<"
              })
            }), _jsx(_components.span, {
              className: "token punctuation",
              children: ">"
            })]
          }), _jsx(_components.span, {
            className: "token plain-text",
            children: "\n"
          })]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token plain-text",
            children: "      Count: "
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "state", _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token property-access",
            children: "count"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token plain-text",
            children: "\n"
          })]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token plain-text",
            children: "      "
          }), _jsxs(_components.span, {
            className: "token tag",
            children: [_jsxs(_components.span, {
              className: "token tag",
              children: [_jsx(_components.span, {
                className: "token punctuation",
                children: "<"
              }), "button"]
            }), " ", _jsx(_components.span, {
              className: "token attr-name",
              children: "onClick"
            }), _jsxs(_components.span, {
              className: "token script language-javascript",
              children: [_jsx(_components.span, {
                className: "token script-punctuation punctuation",
                children: "="
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "{"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "("
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: ")"
              }), " ", _jsx(_components.span, {
                className: "token arrow operator",
                children: "=>"
              }), " ", _jsx(_components.span, {
                className: "token function",
                children: "dispatch"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "("
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "{"
              }), _jsx(_components.span, {
                className: "token literal-property property",
                children: "type"
              }), _jsx(_components.span, {
                className: "token operator",
                children: ":"
              }), " ", _jsx(_components.span, {
                className: "token string",
                children: "\"increment\""
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "}"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: ")"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "}"
              })]
            }), _jsx(_components.span, {
              className: "token punctuation",
              children: ">"
            })]
          }), _jsx(_components.span, {
            className: "token plain-text",
            children: "+"
          }), _jsxs(_components.span, {
            className: "token tag",
            children: [_jsxs(_components.span, {
              className: "token tag",
              children: [_jsx(_components.span, {
                className: "token punctuation",
                children: "</"
              }), "button"]
            }), _jsx(_components.span, {
              className: "token punctuation",
              children: ">"
            })]
          }), _jsx(_components.span, {
            className: "token plain-text",
            children: "\n"
          })]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token plain-text",
            children: "      "
          }), _jsxs(_components.span, {
            className: "token tag",
            children: [_jsxs(_components.span, {
              className: "token tag",
              children: [_jsx(_components.span, {
                className: "token punctuation",
                children: "<"
              }), "button"]
            }), " ", _jsx(_components.span, {
              className: "token attr-name",
              children: "onClick"
            }), _jsxs(_components.span, {
              className: "token script language-javascript",
              children: [_jsx(_components.span, {
                className: "token script-punctuation punctuation",
                children: "="
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "{"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "("
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: ")"
              }), " ", _jsx(_components.span, {
                className: "token arrow operator",
                children: "=>"
              }), " ", _jsx(_components.span, {
                className: "token function",
                children: "dispatch"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "("
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "{"
              }), _jsx(_components.span, {
                className: "token literal-property property",
                children: "type"
              }), _jsx(_components.span, {
                className: "token operator",
                children: ":"
              }), " ", _jsx(_components.span, {
                className: "token string",
                children: "\"decrement\""
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "}"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: ")"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "}"
              })]
            }), _jsx(_components.span, {
              className: "token punctuation",
              children: ">"
            })]
          }), _jsx(_components.span, {
            className: "token plain-text",
            children: "-"
          }), _jsxs(_components.span, {
            className: "token tag",
            children: [_jsxs(_components.span, {
              className: "token tag",
              children: [_jsx(_components.span, {
                className: "token punctuation",
                children: "</"
              }), "button"]
            }), _jsx(_components.span, {
              className: "token punctuation",
              children: ">"
            })]
          }), _jsx(_components.span, {
            className: "token plain-text",
            children: "\n"
          })]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token plain-text",
            children: "    "
          }), _jsxs(_components.span, {
            className: "token tag",
            children: [_jsx(_components.span, {
              className: "token tag",
              children: _jsx(_components.span, {
                className: "token punctuation",
                children: "</"
              })
            }), _jsx(_components.span, {
              className: "token punctuation",
              children: ">"
            })]
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        })]
      })
    }), "\n", _jsxs(_components.h3, {
      id: "dos-and-donts",
      children: ["Do’s and don’ts", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Do’s and don’ts\"",
        href: "#dos-and-donts",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsxs(_components.h4, {
      id: "never-render-a-list-of-children-without-assigning-a-unique-key-to-each",
      children: ["Never render a list of children without assigning a unique key to each", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Never render a list of children without assigning a unique key to each\"",
        href: "#never-render-a-list-of-children-without-assigning-a-unique-key-to-each",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsx(_components.p, {
      children: "This can have a huge impact on the performance, even bigger if you render a big list of elements. As from React documentation:"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "Keys help React identify which items have changed, are added, or are removed. Keys should be given to the elements inside the array to give the elements a stable identity. The best way to pick a key is to use a string that uniquely identifies a list item among its siblings. Most often you would use IDs from your data as keys."
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Don’t:"
      })
    }), "\n", _jsx(_components.pre, {
      className: "language-jsx",
      children: _jsxs(_components.code, {
        className: "language-jsx code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "class"
          }), " ", _jsx(_components.span, {
            className: "token class-name",
            children: "FooComponent"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "extends"
          }), " ", _jsxs(_components.span, {
            className: "token class-name",
            children: ["React", _jsx(_components.span, {
              className: "token punctuation",
              children: "."
            }), "Component"]
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token function",
            children: "render"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword control-flow",
            children: "return"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "this"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token property-access",
            children: "props"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token property-access",
            children: "data"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token method function property-access",
            children: "map"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token parameter",
            children: "item"
          }), " ", _jsx(_components.span, {
            className: "token arrow operator",
            children: "=>"
          }), " ", _jsxs(_components.span, {
            className: "token tag",
            children: [_jsxs(_components.span, {
              className: "token tag",
              children: [_jsx(_components.span, {
                className: "token punctuation",
                children: "<"
              }), _jsx(_components.span, {
                className: "token class-name",
                children: "Item"
              })]
            }), " ", _jsx(_components.span, {
              className: "token attr-name",
              children: "data"
            }), _jsxs(_components.span, {
              className: "token script language-javascript",
              children: [_jsx(_components.span, {
                className: "token script-punctuation punctuation",
                children: "="
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "{"
              }), "item", _jsx(_components.span, {
                className: "token punctuation",
                children: "}"
              })]
            }), " ", _jsx(_components.span, {
              className: "token punctuation",
              children: "/>"
            })]
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "class"
          }), " ", _jsx(_components.span, {
            className: "token class-name",
            children: "FooComponent"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "extends"
          }), " ", _jsxs(_components.span, {
            className: "token class-name",
            children: ["React", _jsx(_components.span, {
              className: "token punctuation",
              children: "."
            }), "Component"]
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token function",
            children: "render"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword control-flow",
            children: "return"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "this"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token property-access",
            children: "props"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token property-access",
            children: "data"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token method function property-access",
            children: "map"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsxs(_components.span, {
            className: "token parameter",
            children: ["item", _jsx(_components.span, {
              className: "token punctuation",
              children: ","
            }), " index"]
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token arrow operator",
            children: "=>"
          }), " ", _jsxs(_components.span, {
            className: "token tag",
            children: [_jsxs(_components.span, {
              className: "token tag",
              children: [_jsx(_components.span, {
                className: "token punctuation",
                children: "<"
              }), _jsx(_components.span, {
                className: "token class-name",
                children: "Item"
              })]
            }), " ", _jsx(_components.span, {
              className: "token attr-name",
              children: "key"
            }), _jsxs(_components.span, {
              className: "token script language-javascript",
              children: [_jsx(_components.span, {
                className: "token script-punctuation punctuation",
                children: "="
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "{"
              }), "index", _jsx(_components.span, {
                className: "token punctuation",
                children: "}"
              })]
            }), " ", _jsx(_components.span, {
              className: "token attr-name",
              children: "data"
            }), _jsxs(_components.span, {
              className: "token script language-javascript",
              children: [_jsx(_components.span, {
                className: "token script-punctuation punctuation",
                children: "="
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "{"
              }), "item", _jsx(_components.span, {
                className: "token punctuation",
                children: "}"
              })]
            }), " ", _jsx(_components.span, {
              className: "token punctuation",
              children: "/>"
            })]
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        })]
      })
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Do:"
      })
    }), "\n", _jsx(_components.pre, {
      className: "language-jsx",
      children: _jsxs(_components.code, {
        className: "language-jsx code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "class"
          }), " ", _jsx(_components.span, {
            className: "token class-name",
            children: "FooComponent"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "extends"
          }), " ", _jsxs(_components.span, {
            className: "token class-name",
            children: ["React", _jsx(_components.span, {
              className: "token punctuation",
              children: "."
            }), "Component"]
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token function",
            children: "render"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword control-flow",
            children: "return"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "this"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token property-access",
            children: "props"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token property-access",
            children: "data"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token method function property-access",
            children: "map"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token parameter",
            children: "item"
          }), " ", _jsx(_components.span, {
            className: "token arrow operator",
            children: "=>"
          }), " ", _jsxs(_components.span, {
            className: "token tag",
            children: [_jsxs(_components.span, {
              className: "token tag",
              children: [_jsx(_components.span, {
                className: "token punctuation",
                children: "<"
              }), _jsx(_components.span, {
                className: "token class-name",
                children: "Item"
              })]
            }), " ", _jsx(_components.span, {
              className: "token attr-name",
              children: "key"
            }), _jsxs(_components.span, {
              className: "token script language-javascript",
              children: [_jsx(_components.span, {
                className: "token script-punctuation punctuation",
                children: "="
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "{"
              }), "item", _jsx(_components.span, {
                className: "token punctuation",
                children: "."
              }), _jsx(_components.span, {
                className: "token property-access",
                children: "id"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "}"
              })]
            }), " ", _jsx(_components.span, {
              className: "token attr-name",
              children: "data"
            }), _jsxs(_components.span, {
              className: "token script language-javascript",
              children: [_jsx(_components.span, {
                className: "token script-punctuation punctuation",
                children: "="
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "{"
              }), "item", _jsx(_components.span, {
                className: "token punctuation",
                children: "}"
              })]
            }), " ", _jsx(_components.span, {
              className: "token punctuation",
              children: "/>"
            })]
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        })]
      })
    }), "\n", _jsxs(_components.h4, {
      id: "never-create-functions-or-objects-in-props",
      children: ["Never create functions or objects in props", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Never create functions or objects in props\"",
        href: "#never-create-functions-or-objects-in-props",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsx(_components.p, {
      children: "This can have a huge impact on the performance. If you create new objects or functions in the props, a new reference will be passed down to the child each time its parents re-render, resulting in unnecessary re-renders and probably more unwanted behaviors."
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Don’t:"
      })
    }), "\n", _jsx(_components.pre, {
      className: "language-jsx",
      children: _jsxs(_components.code, {
        className: "language-jsx code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "class"
          }), " ", _jsx(_components.span, {
            className: "token class-name",
            children: "FooComponent"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "extends"
          }), " ", _jsxs(_components.span, {
            className: "token class-name",
            children: ["React", _jsx(_components.span, {
              className: "token punctuation",
              children: "."
            }), "Component"]
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token function",
            children: "render"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword control-flow",
            children: "return"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsxs(_components.span, {
            className: "token tag",
            children: [_jsxs(_components.span, {
              className: "token tag",
              children: [_jsx(_components.span, {
                className: "token punctuation",
                children: "<"
              }), _jsx(_components.span, {
                className: "token class-name",
                children: "FooChild"
              })]
            }), "\n"]
          })]
        }), _jsx(_components.span, {
          className: "code-line",
          children: _jsxs(_components.span, {
            className: "token tag",
            children: ["        ", _jsx(_components.span, {
              className: "token attr-name",
              children: "onClick"
            }), _jsxs(_components.span, {
              className: "token script language-javascript",
              children: [_jsx(_components.span, {
                className: "token script-punctuation punctuation",
                children: "="
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "{"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "("
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: ")"
              }), " ", _jsx(_components.span, {
                className: "token arrow operator",
                children: "=>"
              }), " ", _jsx(_components.span, {
                className: "token keyword",
                children: "this"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "."
              }), _jsx(_components.span, {
                className: "token property-access",
                children: "props"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "."
              }), _jsx(_components.span, {
                className: "token method function property-access",
                children: "handleClick"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "("
              }), _jsx(_components.span, {
                className: "token spread operator",
                children: "..."
              }), "args", _jsx(_components.span, {
                className: "token punctuation",
                children: ")"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "}"
              })]
            }), "\n"]
          })
        }), _jsx(_components.span, {
          className: "code-line",
          children: _jsxs(_components.span, {
            className: "token tag",
            children: ["        ", _jsx(_components.span, {
              className: "token attr-name",
              children: "data"
            }), _jsxs(_components.span, {
              className: "token script language-javascript",
              children: [_jsx(_components.span, {
                className: "token script-punctuation punctuation",
                children: "="
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "{"
              }), _jsx(_components.span, {
                className: "token keyword",
                children: "this"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "."
              }), _jsx(_components.span, {
                className: "token property-access",
                children: "props"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "."
              }), _jsx(_components.span, {
                className: "token method function property-access",
                children: "filter"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "("
              }), _jsx(_components.span, {
                className: "token parameter",
                children: "item"
              }), " ", _jsx(_components.span, {
                className: "token arrow operator",
                children: "=>"
              }), " item", _jsx(_components.span, {
                className: "token punctuation",
                children: "."
              }), _jsx(_components.span, {
                className: "token property-access",
                children: "id"
              }), " ", _jsx(_components.span, {
                className: "token operator",
                children: "==="
              }), " ", _jsx(_components.span, {
                className: "token number",
                children: "5"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: ")"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "}"
              })]
            }), "\n"]
          })
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsxs(_components.span, {
            className: "token tag",
            children: ["      ", _jsx(_components.span, {
              className: "token punctuation",
              children: "/>"
            })]
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        })]
      })
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Do:"
      })
    }), "\n", _jsx(_components.pre, {
      className: "language-jsx",
      children: _jsxs(_components.code, {
        className: "language-jsx code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "class"
          }), " ", _jsx(_components.span, {
            className: "token class-name",
            children: "FooComponent"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "extends"
          }), " ", _jsxs(_components.span, {
            className: "token class-name",
            children: ["React", _jsx(_components.span, {
              className: "token punctuation",
              children: "."
            }), "Component"]
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token function-variable function",
            children: "onClick"
          }), " ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsxs(_components.span, {
            className: "token parameter",
            children: [_jsx(_components.span, {
              className: "token spread operator",
              children: "..."
            }), "args"]
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token arrow operator",
            children: "=>"
          }), " ", _jsx(_components.span, {
            className: "token parameter",
            children: "event"
          }), " ", _jsx(_components.span, {
            className: "token arrow operator",
            children: "=>"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword control-flow",
            children: "return"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "this"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token property-access",
            children: "props"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token method function property-access",
            children: "handleClick"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token spread operator",
            children: "..."
          }), "args", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsx(_components.span, {
          className: "code-line",
          children: "\n"
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token function",
            children: "render"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token keyword control-flow",
            children: "return"
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "("
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["      ", _jsxs(_components.span, {
            className: "token tag",
            children: [_jsxs(_components.span, {
              className: "token tag",
              children: [_jsx(_components.span, {
                className: "token punctuation",
                children: "<"
              }), _jsx(_components.span, {
                className: "token class-name",
                children: "FooChild"
              })]
            }), "\n"]
          })]
        }), _jsx(_components.span, {
          className: "code-line",
          children: _jsxs(_components.span, {
            className: "token tag",
            children: ["        ", _jsx(_components.span, {
              className: "token attr-name",
              children: "onClick"
            }), _jsxs(_components.span, {
              className: "token script language-javascript",
              children: [_jsx(_components.span, {
                className: "token script-punctuation punctuation",
                children: "="
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "{"
              }), _jsx(_components.span, {
                className: "token keyword",
                children: "this"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "."
              }), _jsx(_components.span, {
                className: "token method function property-access",
                children: "onClick"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "("
              }), _jsx(_components.span, {
                className: "token spread operator",
                children: "..."
              }), "args", _jsx(_components.span, {
                className: "token punctuation",
                children: ")"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "}"
              })]
            }), "\n"]
          })
        }), _jsx(_components.span, {
          className: "code-line",
          children: _jsxs(_components.span, {
            className: "token tag",
            children: ["        ", _jsx(_components.span, {
              className: "token attr-name",
              children: "data"
            }), _jsxs(_components.span, {
              className: "token script language-javascript",
              children: [_jsx(_components.span, {
                className: "token script-punctuation punctuation",
                children: "="
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "{"
              }), _jsx(_components.span, {
                className: "token keyword",
                children: "this"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "."
              }), _jsx(_components.span, {
                className: "token property-access",
                children: "props"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "."
              }), _jsx(_components.span, {
                className: "token property-access",
                children: "filteredData"
              }), _jsx(_components.span, {
                className: "token punctuation",
                children: "}"
              })]
            }), "\n"]
          })
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsxs(_components.span, {
            className: "token tag",
            children: ["      ", _jsx(_components.span, {
              className: "token punctuation",
              children: "/>"
            })]
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token punctuation",
            children: ")"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ";"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        })]
      })
    }), "\n", _jsxs(_components.h4, {
      id: "avoid-duplicating-data-between-props-and-state",
      children: ["Avoid duplicating data between props and state", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Avoid duplicating data between props and state\"",
        href: "#avoid-duplicating-data-between-props-and-state",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["If some data can be derived or calculated directly from the props, it’s unnecessary to replicate this data in the state. Props should be the only source of truth. In fact – if you want to calculate the state based on the received props, you’ll need to create a ", _jsx(_components.code, {
        children: "componentDidUpdate"
      }), " method and keep your state and props in sync – this is an anti-pattern."]
    }), "\n", _jsx(_components.p, {
      children: "The only case when assigning props to a state is acceptable is to pass initial data to a component that doesn’t need to be in sync with the store, e.g. forms."
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Avoid:"
      })
    }), "\n", _jsx(_components.pre, {
      className: "language-jsx",
      children: _jsxs(_components.code, {
        className: "language-jsx code-highlight",
        children: [_jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token keyword",
            children: "class"
          }), " ", _jsx(_components.span, {
            className: "token class-name",
            children: "FooComponent"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "extends"
          }), " ", _jsxs(_components.span, {
            className: "token class-name",
            children: ["React", _jsx(_components.span, {
              className: "token punctuation",
              children: "."
            }), "Component"]
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  state ", _jsx(_components.span, {
            className: "token operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "token punctuation",
            children: "{"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token literal-property property",
            children: "foo"
          }), _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "this"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token property-access",
            children: "props"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token property-access",
            children: "foo"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["    ", _jsx(_components.span, {
            className: "token literal-property property",
            children: "bar"
          }), _jsx(_components.span, {
            className: "token operator",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "token keyword",
            children: "this"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token property-access",
            children: "props"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: "."
          }), _jsx(_components.span, {
            className: "token property-access",
            children: "bar"
          }), _jsx(_components.span, {
            className: "token punctuation",
            children: ","
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: ["  ", _jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        }), _jsxs(_components.span, {
          className: "code-line",
          children: [_jsx(_components.span, {
            className: "token punctuation",
            children: "}"
          }), "\n"]
        })]
      })
    }), "\n", _jsxs(_components.h4, {
      id: "avoid-overusing-hocs",
      children: ["Avoid overusing HOCs", _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Avoid overusing HOCs\"",
        href: "#avoid-overusing-hocs",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsx(_components.p, {
      children: "As Michael Jackson (React-Router co-creator) said:"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsxs(_components.p, {
        children: ["“Next time you think you need a HOC (higher-order component) in, you probably don’t. I can do anything you’re doing with your HOC using a regular component with a ", _jsx(_components.a, {
          href: "https://legacy.reactjs.org/docs/render-props.html",
          rel: "nofollow noopener noreferrer",
          target: "_blank",
          children: "render prop"
        }), ".“ – ", _jsx(_components.a, {
          href: "https://twitter.com/mjackson/status/885910553432018945",
          rel: "nofollow noopener noreferrer",
          target: "_blank",
          children: "Michael Jackson"
        })]
      }), "\n"]
    }), "\n", _jsxs(_components.h4, {
      id: "avoid-using-components-without-shouldcomponentupdate",
      children: ["Avoid using Components without ", _jsx(_components.code, {
        children: "shouldComponentUpdate"
      }), _jsx(_components.a, {
        className: "heading-link",
        "aria-label": "Permalink to \"Avoid using Components without \"",
        href: "#avoid-using-components-without-shouldcomponentupdate",
        children: _jsx(_components.svg, {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 16 16",
          width: "16",
          height: "16",
          fill: "currentColor",
          "aria-hidden": "true",
          children: _jsx(_components.path, {
            d: "m7.775 3.275 1.25-1.25a3.5 3.5 0 1 1 4.95 4.95l-2.5 2.5a3.5 3.5 0 0 1-4.95 0 .751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018 1.998 1.998 0 0 0 2.83 0l2.5-2.5a2.002 2.002 0 0 0-2.83-2.83l-1.25 1.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Zm-4.69 9.64a1.998 1.998 0 0 0 2.83 0l1.25-1.25a.751.751 0 0 1 1.042.018.751.751 0 0 1 .018 1.042l-1.25 1.25a3.5 3.5 0 1 1-4.95-4.95l2.5-2.5a3.5 3.5 0 0 1 4.95 0 .751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018 1.998 1.998 0 0 0-2.83 0l-2.5 2.5a1.998 1.998 0 0 0 0 2.83Z"
          })
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["A ", _jsx(_components.a, {
        href: "https://react.dev/reference/react/Component",
        rel: "nofollow noopener noreferrer",
        target: "_blank",
        children: _jsx(_components.code, {
          children: "React.Component"
        })
      }), ", when used without ", _jsx(_components.code, {
        children: "shouldComponentUpdate"
      }), ", will re-render on every prop and state change."]
    }), "\n", _jsxs(_components.ol, {
      children: ["\n", _jsxs(_components.li, {
        children: ["Consider creating a ", _jsx(_components.code, {
          children: "shouldComponentUpdate() "
        }), "method to prevent unnecessary re-renders."]
      }), "\n", _jsxs(_components.li, {
        children: ["Consider using the built-in ", _jsx(_components.code, {
          children: "PureComponent"
        }), " instead of writing ", _jsx(_components.code, {
          children: "shouldComponentUpdate"
        }), " by hand. ", _jsx(_components.code, {
          children: "PureComponent"
        }), " performs a shallow comparison of props and state, and reduces the chance that you’ll skip a necessary update."]
      }), "\n"]
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
